import { SKETCH_MONO, SKETCH_SANS } from "../common/sketchPalette";

/**
 * The diagram the demo animates: a four-step pipeline with a scrub track.
 *
 * It is written to exercise every feature of @penwell/draw-on:
 *   - stroke-only paths (the boxes, arrows, track, ticks) that draw themselves
 *   - fill-only shapes (the version pill, the progress marker) which get a
 *     temporary outline so they have something to draw, then fade their fill in
 *   - <text> labels, which fade in with the fills
 *
 * Colours are passed in rather than inherited from CSS, and the markup is
 * rebuilt per theme, which is also what makes the diagram redraw itself when
 * you switch theme.
 */

const NODES = [
  { x: 36, title: "parse svg", sub: "any inline svg" },
  { x: 214, title: "draw strokes", sub: "one by one" },
  { x: 392, title: "fade fills", sub: "+ labels at 60%" },
  { x: 570, title: "record webm", sub: "no ffmpeg" },
];

const NODE_WIDTH = 154;
const NODE_Y = 96;
const NODE_HEIGHT = 64;

const TICKS = [36, 174, 311, 449, 586, 724];
const TICK_LABELS = ["0", "0.2", "0.4", "0.6", "0.8", "1"];

const buildNodes = ({ ink, quiet }) =>
  NODES.map((node) => {
    const center = node.x + NODE_WIDTH / 2;

    return `
    <rect x="${node.x}" y="${NODE_Y}" width="${NODE_WIDTH}" height="${NODE_HEIGHT}" rx="6" fill="none" stroke="${ink}" stroke-width="1.4" />
    <text x="${center}" y="124" text-anchor="middle" font-family="${SKETCH_SANS}" font-size="14" font-weight="600" fill="${ink}">${node.title}</text>
    <text x="${center}" y="145" text-anchor="middle" font-family="${SKETCH_MONO}" font-size="10.5" fill="${quiet}">${node.sub}</text>`;
  }).join("");

const buildArrows = ({ ink }) =>
  NODES.map((node, index) => {
    if (index === NODES.length - 1) return "";

    const start = node.x + NODE_WIDTH + 5;
    const end = node.x + NODE_WIDTH + 19;
    const mid = end - 6;

    return `
    <path d="M${start} 128 C ${start + 5} ${126 + index}, ${end - 5} ${130 - index}, ${end} 128" fill="none" stroke="${ink}" stroke-width="1.4" stroke-linecap="round" />
    <path d="M${mid} 124 L${end} 128 L${mid} 132" fill="none" stroke="${ink}" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round" />`;
  }).join("");

const buildTicks = ({ quiet }) =>
  TICKS.map(
    (x, index) => `
    <line x1="${x}" y1="232" x2="${x}" y2="248" stroke="${quiet}" stroke-width="1" />
    <text x="${x}" y="266" text-anchor="middle" font-family="${SKETCH_MONO}" font-size="10" fill="${quiet}">${TICK_LABELS[index]}</text>`
  ).join("");

export const buildSketch = ({ ink, quiet, accent, accentInk, wash }) => `
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 760 300" role="img" aria-label="How draw-on works: parse the SVG, draw its strokes, fade the fills and labels in, then record a WebM.">
  <line x1="36" y1="68" x2="724" y2="68" stroke="${quiet}" stroke-width="1" />

  <text x="36" y="44" font-family="${SKETCH_SANS}" font-size="17" font-weight="600" fill="${ink}">how draw-on animates an svg</text>

  <rect x="596" y="24" width="128" height="30" rx="15" fill="${wash}" />
  <text x="660" y="44" text-anchor="middle" font-family="${SKETCH_MONO}" font-size="11.5" fill="${accentInk}">v0.1.0 &#183; MIT</text>

  ${buildNodes({ ink, quiet })}
  ${buildArrows({ ink })}

  <path d="M36 240 C 180 235, 300 245, 449 239 S 640 243, 724 238" fill="none" stroke="${quiet}" stroke-width="1.2" stroke-linecap="round" />
  ${buildTicks({ quiet })}

  <circle cx="449" cy="240" r="7" fill="${accent}" />

  <text x="36" y="222" font-family="${SKETCH_MONO}" font-size="11" fill="${quiet}">setProgress()</text>
  <text x="449" y="222" text-anchor="middle" font-family="${SKETCH_MONO}" font-size="11" fill="${ink}">0.60</text>
</svg>`;

export default buildSketch;